import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { news } from "@/mocks/news";
import { getRelatedNews } from "@/utils/comic";

export default function NewsDetail() {
  const { id } = useParams();
  const item = news.find((n) => n.id === id);

  useDocumentMeta({
    title: item ? `妖精漫画-${item.title}` : "妖精漫画-资讯详情",
    description: item ? item.excerpt : "妖精漫画资讯详情页。",
    keywords: item ? `${item.type},妖精漫画资讯,${item.title}` : "妖精漫画资讯",
  });

  if (!item) {
    return (
      <div className="mx-auto flex max-w-[1280px] flex-col items-center gap-4 px-4 py-24 text-center md:px-6">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 text-primary-600">
          <i className="ri-newspaper-line text-3xl"></i>
        </span>
        <h1 className="font-heading text-2xl text-foreground-950">抱歉，没有找到这条资讯</h1>
        <p className="text-sm text-foreground-500">它可能已被移除，或者链接有误。</p>
        <Link
          to="/news"
          className="mt-2 rounded-md bg-primary-500 px-6 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600 cursor-pointer whitespace-nowrap"
        >
          返回资讯列表
        </Link>
      </div>
    );
  }

  const related = getRelatedNews(news, item, 3);

  return (
    <div className="w-full">
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-[1280px] px-4 py-6 md:px-6 md:py-8">
          <Breadcrumb
            items={[{ label: "首页", path: "/" }, { label: "资讯", path: "/news" }, { label: item.type }]}
          />
        </div>
      </div>

      <article className="mx-auto max-w-[860px] px-4 py-8 md:px-6 md:py-12">
        <span className="inline-block rounded-full bg-primary-100 px-3 py-1 text-xs text-primary-700">
          {item.type}
        </span>
        <h1 className="mt-4 font-heading text-2xl leading-snug text-foreground-950 md:text-4xl">{item.title}</h1>

        <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 border-b border-background-200 pb-5 text-sm text-foreground-500">
          <span className="flex items-center gap-1.5">
            <i className="ri-calendar-line"></i>
            {item.date}
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-user-line"></i>
            {item.author}
          </span>
        </div>

        <div className="mt-6 overflow-hidden rounded-lg">
          <img
            src={item.cover}
            alt={`${item.title} 配图`}
            title={`${item.title} 妖精漫画资讯`}
            className="h-auto w-full object-cover object-top"
          />
        </div>

        <div className="mt-8 flex flex-col gap-6">
          <p className="rounded-lg border-l-4 border-primary-400 bg-primary-50 px-5 py-4 text-sm leading-loose text-foreground-700">
            {item.excerpt}
          </p>

          {item.content.map((section, index) =>
            section.image ? (
              <figure key={`img-${index}`} className="overflow-hidden rounded-lg">
                <img
                  src={section.image}
                  alt={`${item.title} 配图 ${index}`}
                  title={`${item.title} 相关配图`}
                  className="h-auto w-full object-cover object-top"
                />
                <figcaption className="mt-2 text-center text-xs text-foreground-400">
                  配图 · 妖精漫画资讯
                </figcaption>
              </figure>
            ) : (
              <p key={`text-${index}`} className="text-base leading-loose text-foreground-700">
                {section.text}
              </p>
            )
          )}
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-4">
          <Link
            to="/news"
            className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            返回资讯列表
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-primary-600 transition-colors hover:text-primary-700 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-home-4-line"></i>
            返回首页
          </Link>
        </div>

        <div className="mt-12 border-t border-background-200 pt-8">
          <h2 className="mb-5 flex items-center gap-2 font-heading text-xl text-foreground-950">
            <i className="ri-article-line text-accent-500"></i>
            相关资讯
          </h2>
          <div className="flex flex-col gap-4">
            {related.map((rel) => (
              <Link
                key={rel.id}
                to={`/news/${rel.id}`}
                className="group flex items-start gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300 cursor-pointer"
              >
                <div className="relative h-16 w-24 shrink-0 overflow-hidden rounded-md">
                  <img
                    src={rel.cover}
                    alt={`${rel.title} 配图`}
                    title={`${rel.title} 妖精漫画资讯`}
                    className="h-full w-full object-cover object-top"
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="line-clamp-2 text-sm font-medium text-foreground-950 transition-colors group-hover:text-primary-600">
                    {rel.title}
                  </h3>
                  <p className="mt-1 text-xs text-foreground-400">
                    {rel.type} · {rel.date}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </article>
    </div>
  );
}